import { useState, useEffect, useRef, useCallback } from 'react';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/firebaseConfig';
import { ADMIN_EMAIL } from '../constants';
import { logMonitoringEvent } from '../utils/monitoringLogger';

const googleProvider = new GoogleAuthProvider();

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const loginInProgress = useRef(false);

  // Dev-only mock user via ?mockUser=1 — bypasses Google OAuth for local QA on localhost.
  // Hard-gated to localhost so prod cannot be spoofed.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocal) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('mockUser') !== '1') return;
    const mockUser = {
      id: 'qa-mock-uid',
      name: 'QA Mock',
      email: ADMIN_EMAIL,
      photoURL: null,
      isAdmin: true,
    };
    setUser(mockUser);
    setAuthLoading(false);
  }, []);

  // Auto-resume session via Firebase Auth persistence
  useEffect(() => {
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mockUser') === '1') return; // skip Firebase auth path when mocked
    }
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (loginInProgress.current) return;

      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            setUser({ id: firebaseUser.uid, ...userDoc.data() });
          } else {
            // Authenticated but no Firestore profile yet - create one
            const userData = buildUserData(firebaseUser);
            userData.createdAt = new Date().toISOString();
            await setDoc(doc(db, 'users', firebaseUser.uid), userData);
            setUser({ id: firebaseUser.uid, ...userData });
          }
        } catch (e) {
          console.error('Error loading user profile:', e);
          logMonitoringEvent('error', 'auth', `Error loading user profile: ${e.message}`, {
            userId: firebaseUser.uid,
          });
        }
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = useCallback(async () => {
    loginInProgress.current = true;

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;

      const existingDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      const userData = buildUserData(firebaseUser);
      if (!existingDoc.exists()) {
        userData.createdAt = new Date().toISOString();
      }

      await setDoc(doc(db, 'users', firebaseUser.uid), userData, { merge: true });

      const userObj = { id: firebaseUser.uid, ...userData };
      setUser(userObj);
      setAuthLoading(false);
      return userObj;
    } finally {
      loginInProgress.current = false;
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    setUser(null);
  }, []);

  return { user, authLoading, login, logout };
};

function buildUserData(firebaseUser) {
  return {
    name: firebaseUser.displayName || 'Anonymous',
    email: firebaseUser.email,
    photoURL: firebaseUser.photoURL || null,
    isAdmin: firebaseUser.email === ADMIN_EMAIL,
    updatedAt: new Date().toISOString(),
  };
}
